import { useEffect, useState } from 'react';
import { useAtom, useStore } from 'jotai';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { execute } from '@/graphql/execute';
import { deviationJobsQuery } from '@/queries/deviation.query';
import { surfaceDifferenceJobsQuery } from '@/queries/surfaceDifference.query';
import { layersQuery } from '@/queries/layer.query';
import { getProjectJobsQuery } from '@/queries/project.query';
import { comparisonBatchStatusQuery } from '@/queries/comparison.query';
import {
  comparisonRetryValuesAtom,
  deviationTrackingAtom,
  UNCERTAIN_START_TIMEOUT_MS,
  type DeviationTracker,
} from '@/atoms/deviation-progress.atom';
import {
  COMPARISON_REPORT_START_TIMEOUT_MS,
  reportTrackingAtom,
  withoutReportTracker,
  withReportBatchMoved,
  withReportDeadline,
} from '@/atoms/report-progress.atom';
import { COMPARE_RESULTS } from '@/lib/analyses/compare/availability';
import { translateKey } from '@/lib/analyses/translateKey';
import type { CompareFormValues } from '@/lib/analyses/compare/formSchema';
import { isOptionalStepType } from '@/lib/analyses/optionalSteps';
import { jobErrorMessage } from '@/lib/jobErrorMessage';

/** A paired run's parts, in the dialog's result order (cut & fill first). */
const inPartOrder = (members: DeviationTracker[]) =>
  [...members].sort((a, b) => COMPARE_RESULTS.indexOf(a.part!) - COMPARE_RESULTS.indexOf(b.part!));

/**
 * The loading line of a paired Compare surfaces run (spec §12.7, C5), e.g.
 * "Compare surfaces · North pad · Cut & fill ✓ · Deviation map running…" —
 * named by the run (`sourceLayerName` carries the run name for a group), so
 * two runs in flight are told apart. Each part's state is in words or a
 * glyph, never colour alone. Exported so the dialog opens its
 * `toast.loading` with the same line the hook updates.
 */
export function comparisonToastText(t: (...args: never[]) => unknown, members: DeviationTracker[]): string {
  const parts = inPartOrder(members).map((m) => {
    const part = translateKey(t, m.partLabelKey!);
    if (m.partOutcome?.status === 'done') return translateKey(t, 'compareSurfaces.toast.partDone', { part });
    if (m.partOutcome?.status === 'failed') return translateKey(t, 'compareSurfaces.toast.partFailed', { part });
    return translateKey(t, 'compareSurfaces.toast.partRunning', { part });
  });
  const name = members[0]?.sourceLayerName;
  return name
    ? translateKey(t, 'compareSurfaces.toast.running', { name, parts: parts.join(' · ') })
    : translateKey(t, 'compareSurfaces.toast.runningUnnamed', { parts: parts.join(' · ') });
}

export interface DeviationProgressOptions {
  /**
   * Reopens Compare surfaces with the given values; a paired run that partly
   * failed offers it as "Retry <part>". Without it the action is omitted.
   * Pass a stable callback: it is a dependency of the tracking effect.
   */
  onRetry?: (values: Partial<CompareFormValues>) => void;
  /**
   * Shows a result layer in the viewer; a comparison that finished offers it
   * as "Open" on its cut & fill result (else its deviation map). Without it
   * the action is omitted. Pass a stable callback, as for `onRetry`.
   */
  onOpenLayer?: (layerId: string) => void;
}

/**
 * Mounts once per project shell.
 *
 * Two responsibilities, deliberately decoupled:
 *
 * 1. **Toast updates** — driven by in-memory trackers populated by the
 *    deviation modal's onSuccess. Only this session's jobs get a toast,
 *    which is the desired behaviour (toast IDs are local to sonner).
 *
 * 2. **Badge count** — derived from the live job query, not the trackers,
 *    so reloading the page or starting a job in another tab still surfaces
 *    a badge if the server has anything in WAITING/PROCESSING.
 *
 * ## Polling
 *
 * We do NOT rely on TanStack Query's `refetchInterval` for the deviation /
 * layers queries here. Both query keys (`['deviationJobs', projectId]` and
 * `['layers', projectId]`) are also subscribed by other hooks on the same
 * page (`useLayers` for layers; `AnalysesPage` for deviationJobs). When
 * multiple observers share a query key, TanStack Query's interval
 * coordination is fragile — particularly when the other observers use the
 * callback form of `refetchInterval` that re-evaluates on every cycle.
 * Two earlier attempts to fix the "toast spins forever" bug by tweaking
 * `refetchInterval` here were defeated by the page-level `useLayers`
 * polling predicate returning false in its own callback.
 *
 * The reliable approach is an explicit `setInterval` driven by
 * `hasTrackers`, which calls `invalidateQueries` on both keys. The page's
 * own observers see the invalidation and refetch — we don't have to fight
 * for ownership of the polling cadence.
 */
export function useDeviationProgress(projectId: string, options?: DeviationProgressOptions) {
  const { t } = useTranslation('project');
  const [trackers, setTrackers] = useAtom(deviationTrackingAtom);
  const store = useStore();
  const onRetry = options?.onRetry;
  const onOpenLayer = options?.onOpenLayer;
  const queryClient = useQueryClient();

  const projectTrackers = trackers.filter((tr) => tr.projectId === projectId);
  const hasTrackers = projectTrackers.length > 0;

  // Read the deviation- and surface-difference-job caches to fold server-side
  // state into the polling predicate. Lets the badge survive page reloads — if
  // jobs are running server-side, we should poll even without a local tracker.
  // Note: this is a one-shot cache read; it doesn't subscribe. We re-read it on
  // every render, which is fine because the caches are updated by the page's own
  // query subscribers (e.g. AnalysesPage when mounted).
  const isRunning = (j: { status: string }) => j.status === 'WAITING' || j.status === 'PROCESSING';
  const cachedJobs = queryClient.getQueryData<{ deviationJobs: { status: string }[] }>(['deviationJobs', projectId]);
  const cachedSdJobs = queryClient.getQueryData<{ surfaceDifferenceJobs: { status: string }[] }>([
    'surfaceDifferenceJobs',
    projectId,
  ]);
  const anyServerRunning =
    (cachedJobs?.deviationJobs.some(isRunning) ?? false) ||
    (cachedSdJobs?.surfaceDifferenceJobs.some(isRunning) ?? false);

  const shouldPoll = hasTrackers || anyServerRunning;

  // Read-only subscriptions — `refetch` returned here is what we drive the
  // polling with. Each `refetch()` call hits the network directly,
  // bypassing every observer-coordination question (`invalidateQueries`
  // requires an active observer to actually fire; sharing query keys with
  // the page's own hooks made that fragile across earlier attempts).
  const { data, refetch: refetchJobs } = useQuery({
    queryKey: ['deviationJobs', projectId],
    queryFn: () => execute(deviationJobsQuery, { projectId }),
    enabled: !!projectId,
  });

  // Surface-difference runs reuse this hook's trackers (see
  // RunCompareSurfacesDialog). Their jobs live under a different query, so
  // the failure path needs both lists to find the tracker's batch.
  const { data: sdData, refetch: refetchSdJobs } = useQuery({
    queryKey: ['surfaceDifferenceJobs', projectId],
    queryFn: () => execute(surfaceDifferenceJobsQuery, { projectId }),
    enabled: !!projectId,
  });

  // Clearance runs (see RunClearanceDialog) reuse these trackers too, but
  // CLEARANCE has no dedicated `clearanceJobs(projectId)` resolver the way
  // deviation and surface-difference do — adding one just for a failure
  // check would be a schema change for a single boolean. Falls back to the
  // generic `jobs(projectId)` query instead (`getProjectJobsQuery`, the same
  // one `useReportProgress` polls), filtered to `type === 'CLEARANCE'` below.
  // The SUCCESS path needs it too: postprocess sets the same
  // `metadata.derivedFrom = pointcloudLayerId` on a clearance run's output
  // cloud, so the layer-scan alone cannot tell this run's output from any
  // other run's on the same source — the scan is disambiguated by
  // `metadata.batchId` and gated on this batch's POST job being PROCESSED.
  const { data: projectJobsData, refetch: refetchProjectJobs } = useQuery({
    queryKey: ['projectJobs', projectId],
    queryFn: () => execute(getProjectJobsQuery, { projectId }),
    enabled: !!projectId,
  });

  // R10: a part whose start the server could not confirm. Its preprocess may
  // fail before inserting any job, which no job list shows — only the batch
  // turning FAILED does — so each such batch's status is polled until its
  // part has an outcome. Own query keys, so a plain `refetchInterval` is safe.
  const uncertainBatchIds = projectTrackers
    .filter((tr) => tr.startUncertain && !tr.partOutcome && tr.batchId)
    .map((tr) => tr.batchId);
  const uncertainStatuses = useQueries({
    queries: uncertainBatchIds.map((id) => ({
      queryKey: ['comparisonBatchStatus', id],
      queryFn: () => execute(comparisonBatchStatusQuery, { id }),
      refetchInterval: 2000,
    })),
  });
  const batchesIn = (status: string) =>
    uncertainStatuses.flatMap((q) => (q.data?.batch?.status === status ? [q.data.batch.id] : [])).join(',');
  const failedUncertainBatchIds = batchesIn('FAILED');
  const processingUncertainBatchIds = batchesIn('PROCESSING');

  // Round 3: a preprocess that never ran leaves its batch PROCESSING with no
  // job, for ever. Past `UNCERTAIN_START_TIMEOUT_MS` such a part is failed
  // below. The clock is read on a tick of its own: an unchanged poll leaves
  // every query's data referentially equal, so the tracking effect would not
  // re-run to notice the time passing.
  const [timedOutUncertainBatchIds, setTimedOutUncertainBatchIds] = useState('');
  const hasUncertain = uncertainBatchIds.length > 0;
  useEffect(() => {
    if (!hasUncertain) return;
    const check = () => {
      const now = Date.now();
      setTimedOutUncertainBatchIds(
        store
          .get(deviationTrackingAtom)
          .filter(
            (tr) =>
              tr.startUncertain &&
              !tr.partOutcome &&
              tr.startUncertainAt != null &&
              now - tr.startUncertainAt >= UNCERTAIN_START_TIMEOUT_MS,
          )
          .map((tr) => tr.batchId)
          .sort()
          .join(','),
      );
    };
    check();
    const id = window.setInterval(check, 2000);
    return () => window.clearInterval(id);
  }, [hasUncertain, store]);

  const { data: layersData, refetch: refetchLayers } = useQuery({
    queryKey: ['layers', projectId],
    queryFn: () => execute(layersQuery, { projectId }),
    enabled: !!projectId && hasTrackers,
  });

  // Explicit polling. While `shouldPoll` is true, fire `refetch()` directly
  // every 2 s on both queries. `refetch` ignores `enabled` and any shared
  // observer's options, so a single mounted instance of this hook fully
  // owns the cadence — regardless of what `useLayers` / `AnalysesPage` do
  // with the same query keys. The console-visible network requests are
  // proof the cadence is alive; if you don't see them in DevTools while a
  // tracker exists, the bug is elsewhere (e.g. the modal failed to add a
  // tracker, or `projectId` is empty).
  useEffect(() => {
    if (!shouldPoll || !projectId) return;
    // `cancelRefetch: false` on EVERY one of them. `refetch()` defaults to
    // `true`, which — once the query has data — cancels the request still in
    // flight and starts another. A response slower than this 2 s interval is
    // then never delivered: the tick restarts it for ever, the cached data
    // stays frozen on the snapshot from before the run, and the toast and the
    // dock's overlay badge spin until the tab is reloaded (reported from prod
    // on 2026-09-22, on a project whose `jobs(projectId)` response had grown
    // to ~8 MB). A tick JOINS an in-flight poll instead.
    const tick = () => {
      void refetchJobs({ cancelRefetch: false });
      void refetchSdJobs({ cancelRefetch: false });
      void refetchProjectJobs({ cancelRefetch: false });
      void refetchLayers({ cancelRefetch: false });
    };
    // Fire once immediately so we don't wait 2 s for the first tick — the
    // user just clicked Run and expects to see polling start now.
    tick();
    const id = window.setInterval(tick, 2000);
    return () => window.clearInterval(id);
  }, [shouldPoll, projectId, refetchJobs, refetchSdJobs, refetchProjectJobs, refetchLayers]);

  useEffect(() => {
    if (!hasTrackers) return;

    const jobs = [
      ...(data?.deviationJobs ?? []),
      ...(sdData?.surfaceDifferenceJobs ?? []),
      ...(projectJobsData?.jobs ?? []).filter(
        (j) => j.type === 'CLEARANCE' || j.type === 'LAYER_STACK' || j.type === 'SECTION_OVERLAYS' || j.type === 'MAP_2D',
      ),
    ];
    // Unfiltered project job list. A clearance run is a CHAIN — CLEARANCE
    // (ECS) -> LAS child (PotreeConverter) -> POST (postprocess, which is
    // what actually creates the layer) — so the clearance branches below need
    // to see every job in the batch, not just the `type === 'CLEARANCE'`
    // parent the failure lookup above filters to.
    const allJobs = projectJobsData?.jobs ?? [];
    const layers = layersData?.layers ?? [];
    const idSet = (ids: string) => new Set(ids ? ids.split(',') : []);
    const failedUncertain = idSet(failedUncertainBatchIds);
    const processingUncertain = idSet(processingUncertainBatchIds);
    const timedOutUncertain = idSet(timedOutUncertainBatchIds);

    // The next tracker list is computed HERE, from the store's current value,
    // not inside a `setTrackers` updater: an updater must be pure (StrictMode
    // may call it twice), and every toast / cache / atom write below is a side
    // effect. They are queued on `later` and run once, after the state update.
    const prev = store.get(deviationTrackingAtom);
    const effects: Array<() => void> = [];
    const later = (fn: () => void) => void effects.push(fn);
    const next = [...prev];
    let changed = false;
    // Groups whose parts changed on this poll; settled after the loop.
    const touchedGroups = new Set<string>();
    /** Records a paired run's part outcome instead of settling the shared toast. */
    const recordPart = (i: number, outcome: NonNullable<DeviationTracker['partOutcome']>) => {
      next[i] = { ...next[i], partOutcome: outcome };
      const { groupId, batchId, reportBatchId } = next[i];
      touchedGroups.add(groupId!);
      changed = true;
      // The part whose batch was to receive the combined PDF failed: the
      // server queues it on a part that succeeded instead (`reportRoot` in
      // comparisonReport.ts), so the report tracker follows the part still
      // in the running. With none left the group settles as all-failed and
      // drops the tracker from wherever it now is.
      if (outcome.status !== 'failed' || !reportBatchId || reportBatchId !== batchId) return;
      const survivor = next.find(
        (tr) => tr.groupId === groupId && tr.batchId && tr.batchId !== batchId && tr.partOutcome?.status !== 'failed',
      );
      if (!survivor) return;
      const to = survivor.batchId;
      for (let k = 0; k < next.length; k++)
        if (next[k].groupId === groupId) next[k] = { ...next[k], reportBatchId: to };
      later(() => store.set(reportTrackingAtom, (prev) => withReportBatchMoved(prev, batchId, to)));
    };

    for (let i = next.length - 1; i >= 0; i--) {
      const tracker = next[i];
      if (tracker.projectId !== projectId) continue;
      // A part of a paired run that has already finished waits for its group.
      if (tracker.groupId && tracker.partOutcome) continue;

      /**
       * A SECTION_OVERLAYS re-sample (section-overlays spec §7) resolves
       * BEFORE any of the layer logic below, and never reaches it.
       *
       * It is the one tracked run that mints no layer: it rewrites the two
       * overlay byproducts on the run's own batch and stamps
       * `metadata.sectionOverlays` back onto the SAME result layer the dock
       * is already open on. The layer scan below asks "which NEW layer
       * appeared", which for this run is "none, ever" — so falling through
       * would leave the toast spinning until the tab was closed.
       *
       * The three invalidations are all load-bearing and all different:
       * `layers` refreshes the metadata block (the new `settingsHash`),
       * `batchByproducts` refreshes the signed URLs the chart fetches the
       * file through, and `sectionOverlays` drops the decoded file itself —
       * which is cached with `staleTime: Infinity` under the OLD filenames'
       * key and would otherwise never be re-read.
       */
      // MAP_2D (2D-map spec §4): like a section-overlay re-sample it mints
      // no layer — it stamps `metadata.map2d` on the layer it was run on —
      // so it resolves on its own job and never reaches the layer scan.
      if (tracker.kind === 'map2d') {
        const own = allJobs.filter((j) => j.batchId === tracker.batchId && j.type === 'MAP_2D');
        const failed = own.find((j) => j.status === 'FAILED');
        const done = own.find((j) => j.status === 'PROCESSED');
        if (failed) {
          later(() =>
            toast.error(jobErrorMessage(failed.errorLogs) ?? t('map2d.failedToast'), { id: tracker.toastId, duration: 8000 }),
          );
          next.splice(i, 1);
          changed = true;
          continue;
        }
        if (done) {
          later(() => {
            toast.success(t('map2d.completedToast', { name: tracker.sourceLayerName }), { id: tracker.toastId, duration: 4000 });
            queryClient.invalidateQueries({ queryKey: ['layers', projectId] });
            queryClient.invalidateQueries({ queryKey: ['layersNested', projectId] });
          });
          next.splice(i, 1);
          changed = true;
        }
        continue;
      }

      if (tracker.kind === 'sectionOverlays') {
        const own = allJobs.filter((j) => j.batchId === tracker.batchId && j.type === 'SECTION_OVERLAYS');
        const failed = own.find((j) => j.status === 'FAILED');
        const done = own.find((j) => j.status === 'PROCESSED');
        if (failed) {
          later(() =>
            toast.error(jobErrorMessage(failed.errorLogs) ?? t('stationVolumes.section.overlays.failedToast'), {
              id: tracker.toastId,
              duration: 8000,
            }),
          );
          next.splice(i, 1);
          changed = true;
          continue;
        }
        if (done) {
          later(() => {
            toast.success(t('stationVolumes.section.overlays.completedToast'), {
              id: tracker.toastId,
              duration: 4000,
            });
            queryClient.invalidateQueries({ queryKey: ['layers', projectId] });
            if (tracker.runBatchId) queryClient.invalidateQueries({ queryKey: ['batchByproducts', tracker.runBatchId] });
            queryClient.invalidateQueries({ queryKey: ['sectionOverlays'] });
          });
          next.splice(i, 1);
          changed = true;
        }
        continue;
      }

      // A clearance run is the only kind whose completion is judged on the
      // whole job chain (see the success path below), so it is also the only
      // kind that must fail on ANY job in its batch: with the POST gate in
      // place, a LAS child or a postprocess that dies would otherwise leave
      // the toast spinning forever, because the CLEARANCE parent this hook
      // used to watch alone had already succeeded.
      const isClearance = tracker.kind === 'clearance';
      // A LAYER_STACK run is judged on the same whole-chain basis, and for
      // the same reason: the LAYER_STACK job succeeding only means the
      // worker finished, not that postprocess has minted the result layer
      // (spec §6.2). Watching the parent alone would resolve the toast a
      // beat before there is anything in the tree to click, or leave it
      // spinning for ever if postprocess itself died.
      const isStack = tracker.kind === 'layerStack';
      const wholeChain = isClearance || isStack;
      const batchJobs = wholeChain ? allJobs.filter((j) => j.batchId === tracker.batchId) : [];

      // Failure path: short-circuit on a FAILED deviation job. A part of a
      // Compare surfaces run is judged on its whole batch too (R8): its root
      // can be PROCESSED while a child it needs (a LAS conversion, POST)
      // failed, and watching the root alone then spun for ever. Optional
      // steps (a chained PDF, the renders) never fail the part — the same
      // rule as the hub's (`isOptionalStepType`).
      const job = wholeChain
        ? batchJobs.find((j) => j.status === 'FAILED')
        : tracker.groupId
          ? (jobs.find((j) => j.batchId === tracker.batchId && j.status === 'FAILED') ??
            allJobs.find(
              (j) => j.batchId === tracker.batchId && j.status === 'FAILED' && !isOptionalStepType(j.type),
            ))
          : jobs.find((j) => j.batchId === tracker.batchId);
      if (job?.status === 'FAILED') {
        // `deviationJobs` / `surfaceDifferenceJobs` / the unfiltered
        // project job list all select `errorLogs` now, so it arrives on
        // `job` itself — read it directly rather than looking the same job
        // up by id in a second query's result. Fix round 1: a cross-query
        // lookup here raced the effect's own dependency list (`data` and
        // `projectJobsData` are independent deps at the bottom of this
        // effect) — when the deviation/surface-difference query settled
        // first, the tracker was already spliced out by the time
        // `projectJobsData` caught up, and the sentence was lost for good.
        const specific = jobErrorMessage(job.errorLogs);
        if (tracker.groupId) {
          recordPart(i, { status: 'failed', reason: specific ?? t('compareSurfaces.toast.unknownReason') });
          continue;
        }
        later(() =>
          toast.error(
            specific ??
              t(
                tracker.kind === 'surfaceDifference'
                  ? 'surfaceDifference.failedToast'
                  : tracker.kind === 'clearance'
                    ? 'clearance.dialog.failedToast'
                    : tracker.kind === 'layerStack'
                      ? 'layerStack.dialog.failedToast'
                      : 'deviationAnalysis.failedToast',
                { name: tracker.sourceLayerName },
              ),
            // A sentence the user must act on needs longer than the 5 s a
            // generic "something failed" toast gets.
            { id: tracker.toastId, duration: specific ? 12000 : 5000 },
          ),
        );
        next.splice(i, 1);
        changed = true;
        continue;
      }

      // R10: an uncertain start whose batch FAILED before any job of it ran:
      // preprocess refused it (and recorded it on the group server-side).
      if (
        tracker.groupId &&
        tracker.startUncertain &&
        failedUncertain.has(tracker.batchId) &&
        ![...jobs, ...allJobs].some((j) => j.batchId === tracker.batchId && j.status !== 'WAITING')
      ) {
        recordPart(i, { status: 'failed', reason: t('compareSurfaces.toast.notStarted') });
        continue;
      }

      // Round 3: an uncertain start that, 5 minutes on, still has no job and
      // a PROCESSING batch: its preprocess never ran, and nothing will ever
      // move it. Failed, so the group settles and offers Retry.
      if (
        tracker.groupId &&
        tracker.startUncertain &&
        timedOutUncertain.has(tracker.batchId) &&
        processingUncertain.has(tracker.batchId) &&
        ![...jobs, ...allJobs].some((j) => j.batchId === tracker.batchId)
      ) {
        recordPart(i, { status: 'failed', reason: t('compareSurfaces.toast.startTimedOut') });
        continue;
      }

      // Success path: the LAYER produced by postprocess has appeared.
      // Match by metadata.derivedFrom (set by postprocess from the
      // deviationContext). Disambiguate against pre-existing derived layers
      // by checking the snapshot the modal captured — the new layer is one
      // whose id isn't in `existingDerivedLayerIds`. We deliberately do NOT
      // compare createdAt to a client-captured `startedAt` because the two
      // clocks can disagree by several seconds, which previously caused the
      // toast to spin forever (client ahead) or resolve against a stale
      // layer (client behind).
      //
      // `derivedFrom` + the snapshot alone is a LAYER-SHAPE test, not a
      // THIS-RUN test: every clearance / deviation / ground-classify /
      // rainfall run on the same cloud stamps the same `derivedFrom`, and
      // the snapshot only knows what the client had already fetched when
      // Run was clicked (`useLayers` refetches on mount + invalidation, it
      // does not poll). So a derived layer belonging to a DIFFERENT run —
      // one produced by a run started moments earlier, or one that landed
      // between the last layer fetch and the click — passed both halves and
      // flipped the toast to "complete" while this run's CLEARANCE job was
      // still on ECS. Clearance runs therefore also match on
      // `metadata.batchId`, which postprocess stamps from the leaf job
      // (`metadata.batchId = leafJob.batchId`, postprocess.ts) and which is
      // exactly the batch id `startClearance` returned to the dialog.
      // Treated as a filter only WHEN PRESENT so a layer written by a server
      // build that predates that stamp still resolves rather than hanging.
      const existingIds = new Set(tracker.existingDerivedLayerIds);
      const newLayer = layers.find((l) => {
        const meta = (l.metadata ?? {}) as {
          derivedFrom?: string;
          batchId?: string;
          type?: string;
          scalarAttribute?: string;
          surfaceDifference?: unknown;
        };
        // A stack has N source surfaces and no single layer to be derived
        // FROM, so `postprocess.ts` stamps no `derivedFrom` on the one
        // layer it mints. The batch id it does stamp is the id
        // `startLayerStack` returned to the dialog, which identifies THIS
        // run exactly — a stricter test than `derivedFrom` ever was.
        if (isStack) return meta.batchId === tracker.batchId && meta.type === 'LAYER_STACK' && !existingIds.has(l.id);
        // A part of a paired run settles on its OWN batch's PRIMARY layer
        // only. Both parts usually derive from the same layer, and the
        // GeoTIFF tile layers postprocess mints beside them carry the same
        // `derivedFrom` but NO `batchId` — so the lenient "batchId when
        // present" rule below would let the other part's tile settle this
        // one. Primary = the deviation cloud (the only layer postprocess
        // gives `scalarAttribute: 'Deviation'`) or the cut & fill GLB (the
        // only one carrying a `surfaceDifference` block).
        if (tracker.groupId) {
          if (meta.batchId !== tracker.batchId || existingIds.has(l.id)) return false;
          return tracker.part === 'cutFill'
            ? typeof meta.surfaceDifference === 'object' && meta.surfaceDifference !== null
            : meta.scalarAttribute === 'Deviation';
        }
        if (meta.derivedFrom !== tracker.sourceLayerId) return false;
        if (existingIds.has(l.id)) return false;
        if (isClearance && typeof meta.batchId === 'string' && meta.batchId !== tracker.batchId) return false;
        return true;
      });
      // ...and the chain has to be FINISHED, not merely far enough along to
      // have inserted the layer row. postprocess inserts the layers first
      // and only then marks its own POST job PROCESSED and the batch
      // PROCESSED (postprocess.ts, `job.completeSuccess` at the end), so
      // "the batch's POST job is PROCESSED" is the one client-visible signal
      // that means postprocess itself is done. The POST job does not exist
      // at all until the batch's last leaf finishes, so an empty/POST-less
      // batch view can never be mistaken for a finished one.
      const chainFinished = !wholeChain || batchJobs.some((j) => j.type === 'POST' && j.status === 'PROCESSED');
      if (newLayer && chainFinished && tracker.groupId) {
        recordPart(i, { status: 'done', layerId: newLayer.id });
        continue;
      }
      if (newLayer && chainFinished) {
        later(() => {
          toast.success(
            t(
              tracker.kind === 'surfaceDifference'
                ? 'surfaceDifference.completedToast'
                : tracker.kind === 'clearance'
                  ? 'clearance.dialog.completedToast'
                  : tracker.kind === 'layerStack'
                    ? 'layerStack.dialog.completedToast'
                    : 'deviationAnalysis.completedToast',
              { name: tracker.sourceLayerName },
            ),
            { id: tracker.toastId, duration: 5000 },
          );
          queryClient.invalidateQueries({ queryKey: ['layers', projectId] });
        });
        next.splice(i, 1);
        changed = true;
      }
    }

    // Every group of this project, including one none of whose parts was
    // polled this time (all its parts may have failed to start).
    const groupIds = new Set(next.filter((tr) => tr.projectId === projectId && tr.groupId).map((tr) => tr.groupId!));
    for (const groupId of groupIds) {
      const members = next.filter((tr) => tr.groupId === groupId);
      const { toastId } = members[0];
      if (members.some((m) => !m.partOutcome)) {
        // Still running: re-issue the loading line only now that a part's
        // state changed, so a poll that changed nothing touches no toast.
        if (touchedGroups.has(groupId)) {
          const text = comparisonToastText(t, members);
          later(() => toast.loading(text, { id: toastId, duration: Infinity }));
        }
        continue;
      }
      later(() => settleComparison(members));
      for (let i = next.length - 1; i >= 0; i--) if (next[i].groupId === groupId) next.splice(i, 1);
      changed = true;
    }

    if (changed) setTrackers(next);
    for (const run of effects) run();

    /** Every part of a paired run has finished: one success, one error, or a mixed error with Retry. */
    function settleComparison(members: DeviationTracker[]) {
      const { toastId, groupId, reportBatchId, sourceLayerName } = members[0];
      const retryValues = store.get(comparisonRetryValuesAtom)[groupId!];
      store.set(comparisonRetryValuesAtom, (prev) => {
        const rest = { ...prev };
        delete rest[groupId!];
        return rest;
      });

      const failed = inPartOrder(members).filter((m) => m.partOutcome?.status === 'failed');
      // R9: some part succeeded, so a combined PDF should now be queued
      // server-side. Should that never happen (a lost claim), its tracker
      // must not poll for ever: give it a deadline to see its job appear.
      if (reportBatchId && failed.length < members.length)
        store.set(reportTrackingAtom, (prev) =>
          withReportDeadline(prev, reportBatchId, Date.now() + COMPARISON_REPORT_START_TIMEOUT_MS),
        );
      if (failed.length === 0) {
        // "Open" shows the cut & fill result, else the deviation map (C5).
        const openId = inPartOrder(members)
          .map((m) => (m.partOutcome?.status === 'done' ? m.partOutcome.layerId : undefined))
          .find((id): id is string => !!id);
        toast.success(t('compareSurfaces.toast.completed', { name: sourceLayerName }), {
          id: toastId,
          duration: 5000,
          ...(onOpenLayer &&
            openId && { action: { label: t('compareSurfaces.toast.open'), onClick: () => onOpenLayer(openId) } }),
        });
        queryClient.invalidateQueries({ queryKey: ['layers', projectId] });
        return;
      }

      const description = failed
        .map((m) =>
          t('compareSurfaces.toast.partFailedReason', {
            part: translateKey(t, m.partLabelKey!),
            reason: m.partOutcome?.status === 'failed' ? m.partOutcome.reason : '',
          }),
        )
        .join(' · ');

      if (failed.length === members.length) {
        // Nothing succeeded, so no combined PDF will ever be queued: drop its
        // tracker rather than leave it polling for ever.
        if (reportBatchId) store.set(reportTrackingAtom, (prev) => withoutReportTracker(prev, reportBatchId));
        toast.error(t('compareSurfaces.toast.allFailed', { name: sourceLayerName }), {
          id: toastId,
          description,
          duration: 12000,
        });
        return;
      }

      queryClient.invalidateQueries({ queryKey: ['layers', projectId] });
      const retryParts = failed.map((m) => m.part!);
      // A combined PDF is still coming (one part succeeded): a Retry is a run
      // of its own, so say it will not be in that report (D5).
      const reportComing =
        !!reportBatchId && store.get(reportTrackingAtom).some((r) => r.batchId === reportBatchId);
      const retryNote = onRetry && reportComing ? t('compareSurfaces.toast.retrySeparate') : null;
      toast.error(t('compareSurfaces.toast.finishedWithFailures', { name: sourceLayerName, count: failed.length }), {
        id: toastId,
        description: retryNote ? `${description}\n${retryNote}` : description,
        ...(retryNote && { classNames: { description: 'whitespace-pre-line' } }),
        // Long enough to read the reason and reach Retry (the Toaster has no
        // close button, so an endless toast could only be swiped away).
        duration: 30000,
        ...(onRetry && {
          action: {
            label: t('compareSurfaces.toast.retry', {
              part: failed.map((m) => translateKey(t, m.partLabelKey!)).join(' · '),
            }),
            onClick: () => onRetry({ ...retryValues, results: retryParts }),
          },
        }),
      });
    }
  }, [
    data,
    sdData,
    projectJobsData,
    layersData,
    failedUncertainBatchIds,
    processingUncertainBatchIds,
    timedOutUncertainBatchIds,
    hasTrackers,
    projectId,
    queryClient,
    setTrackers,
    store,
    onRetry,
    onOpenLayer,
    t,
  ]);

  // Badge derives from server state so it survives reloads / picks up jobs
  // started in another tab. Counts both analysis kinds — the badge is a single
  // "analyses running" indicator, not a deviation-only one.
  const runningCount =
    (data?.deviationJobs.filter(isRunning).length ?? 0) + (sdData?.surfaceDifferenceJobs.filter(isRunning).length ?? 0);

  return { runningCount };
}
